/* oxlint-disable max-lines-per-function, complexity */
/* oxlint-disable jsx-a11y/anchor-is-valid, jsx-a11y/control-has-associated-label, jsx-a11y/no-noninteractive-element-to-interactive-role */
import { useMemo, lazy, Suspense, memo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation, Trans } from 'react-i18next';
import clsx from 'clsx';
import backCaret from 'images/back_caret.png';
import shareIcon from 'images/share.png';
import bookmarkChecked from 'images/bookmark_checked.png';
import bookmarkUnchecked from 'images/bookmark_unchecked.png';
import homeBackLarge from 'vectors/home_back_large.svg';
import homeBackLargeAlt from 'vectors/home_back_large_alternate_colors.svg';
import { MessagesButton } from 'Messaging';
import { selectInGhostMode } from 'Users/ghostModeSlice';
import { AppMenu } from '../AppMenu';
import { AppMenuUser } from '../AppMenuUser';
import { useAppHeader } from '../../hooks';
import { type AppHeaderPresentationProps } from './AppHeader.types';

// Lazy load Angular wrapper components to avoid loading Angular modules at import time
// This prevents the Angular dependency chain from being pulled in for tests that
// don't need the lesson player/editor headers
const LessonPlayerHeader = lazy(() => import('../LessonPlayerHeader').then(m => ({ default: m.LessonPlayerHeader })));
const LessonEditorHeader = lazy(() => import('../LessonEditorHeader').then(m => ({ default: m.LessonEditorHeader })));

// Presentation component - renders default layout header based on explicit props
export const AppHeaderPresentation = memo(
    ({
        layout,
        brandStyleClass,
        brandNameShort,
        appHeaderLearnerLayoutLogo,
        appHeaderWordMarkSrc,
        appHeaderWordMarkStyles,
        logoAlt,
        ghostMode,
        userEmail,
        title,
        titleOpts,
        showAlternateHomeButton,
        showCaret: _showCaret,
        viewedStream,
        allowTapScroll,
        currentPath,
        inOfflineMode,
        isMobileApp,
        onGoHome,
        onFollowLearningBreadcrumbsBack,
        onScrollToTop,
        onMobileSettingsMenuClick,
        onShareStream,
        onToggleBookmark,
        navigationMenu,
        userMenu,
        showMessagesButton = true,
    }: AppHeaderPresentationProps) => {
        const { t } = useTranslation();

        const appHeaderClasses = clsx(
            // SCSS classes that must remain:
            // - 'app-header' for .header-invisible .app-header { display: none } rule
            // - brandStyleClass for valar: Tailwind variant to work
            'app-header',
            layout, // Keep for test compatibility (e.g., 'default', 'lesson-player')
            brandStyleClass,
            ghostMode && 'ghost-mode', // Keep for test compatibility
            // Tailwind classes
            'w-full',
            'p-0',
            'm-0',
            // Note: Don't use Tailwind 'block' here - it would override
            // the .header-invisible .app-header { display: none } CSS rule
            'h-10', // 40px mobile
            'sm:h-[75px]', // 75px desktop
        );

        const messagingOpen = useSelector((state: { messaging?: { openChat?: boolean } }) => state.messaging?.openChat);
        const mobileMenuActive = currentPath?.startsWith('/settings') && !messagingOpen;
        // Only render default layout
        if (layout !== 'default') return null;

        return (
            <div className={clsx(appHeaderClasses)}>
                {/* Login-As User info */}
                {ghostMode && (
                    <div
                        className={clsx(
                            // Tailwind (replaces .ghost-mode-label)
                            'absolute',
                            'top-0',
                            'left-1/2',
                            '-translate-x-1/2',
                            'px-1.5',
                            'z-[4]',
                            'text-[11px]',
                            'leading-4',
                            'bg-yellow',
                            'text-black',
                            'text-center',
                        )}
                    >
                        <Trans
                            i18nKey="navigation.app_header.logged_in_as_x_react"
                            values={{ email: userEmail }}
                            components={{ strong: <strong /> }}
                        />
                    </div>
                )}

                {/* Tap bar for screen-scrolling */}
                {allowTapScroll && (
                    <div
                        className={clsx(
                            'tap-scroll-container', // Keep for test compatibility
                            'z-[5]',
                            'h-[15px]',
                            'absolute',
                            'cursor-none',
                            'left-[50px]',
                            'right-[50px]',
                            'sm:left-[160px]',
                            'sm:right-[70px]',
                            'rtl:left-[70px]',
                            'rtl:right-[160px]',
                            'max-sm:rtl:left-[50px]',
                            'max-sm:rtl:right-[50px]',
                        )}
                        onClick={() => onScrollToTop(true)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') onScrollToTop(true);
                        }}
                    />
                )}

                {/* Main header for default layout */}
                <div
                    className={clsx(
                        // Tailwind (replaces .main-header) - structure
                        'w-full',
                        'h-full',
                        'relative',
                        // Mobile: brand color background
                        'max-sm:bg-coral',
                        'valar:max-sm:bg-blue',
                        'max-sm:border-b',
                        'max-sm:border-coral-dark/50',
                        'valar:max-sm:border-blue-dark/50',
                        // Desktop: white background with bottom border
                        'sm:bg-white',
                        'sm:border-b',
                        'sm:border-slate-grey-200',
                        'sm:transition-[border,padding,margin,width,height,opacity]',
                        'sm:duration-300',
                    )}
                >
                    {/* Mobile title */}
                    <span
                        className={clsx(
                            'title', // Keep for test compatibility
                            'text-white',
                            'text-center', // from .center class
                            'w-[38%]',
                            'absolute',
                            'top-1/2',
                            'left-1/2',
                            '-translate-x-1/2',
                            '-translate-y-1/2',
                            'font-semibold',
                            'text-2xs', // 12px
                            'leading-3',
                            titleOpts?.small && 'small text-3xs', // 10px for small variant, keep 'small' for test compatibility
                            'sm:text-lg', // 20px on desktop
                            'sm:leading-5',
                            'sm:w-[45%]',
                            'sm:hidden', // visible-xs replacement
                        )}
                        dangerouslySetInnerHTML={{ __html: title || '' }}
                    />

                    {/* Home Button for outer screens */}
                    {!showAlternateHomeButton && (
                        <a
                            dir="ltr"
                            className={clsx(
                                'home-link', // Keep for test compatibility
                                'absolute',
                                'top-0',
                                'left-0',
                                'py-[17px]',
                                'max-sm:px-3.5', // 14px on mobile
                                'px-[15px]', // 768-830px: tighter padding
                                'min-[830px]:px-7.5', // 830px+: 30px
                                'transition-all',
                                'duration-200',
                                'no-underline',
                                'max-sm:hidden', // hidden-xs replacement
                                'rtl:right-0',
                                'rtl:left-auto',
                            )}
                            onClick={onGoHome}
                            role="button"
                            tabIndex={0}
                            onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') onGoHome();
                            }}
                        >
                            <div>
                                {appHeaderLearnerLayoutLogo && (
                                    <img
                                        className="inline-block align-middle"
                                        src={appHeaderLearnerLayoutLogo}
                                        width="25"
                                        height="40"
                                        alt={logoAlt}
                                    />
                                )}
                                <img
                                    className={clsx(
                                        // Tailwind (replaces .wordmark)
                                        'ml-3', // 12px
                                        'w-[125px]',
                                        'h-10',
                                        'object-contain',
                                        'max-sm:hidden',
                                        'inline-block',
                                        'align-middle',
                                        // Per-brand vertical adjustment for wordmark logos
                                        brandStyleClass === 'valar' && 'translate-y-[7px]',
                                        brandStyleClass === 'quantic' && '-translate-y-px',
                                    )}
                                    src={appHeaderWordMarkSrc}
                                    style={appHeaderWordMarkStyles}
                                    alt={brandNameShort}
                                />
                            </div>
                        </a>
                    )}

                    {/* Alternate Home Button for inner screens */}
                    {showAlternateHomeButton && (
                        <>
                            <a
                                className={clsx(
                                    'home-link', // Keep for test compatibility
                                    'alternate', // Keep for test compatibility
                                    'absolute',
                                    'top-0',
                                    'left-0',
                                    'h-[75px]',
                                    'px-7.5',
                                    'no-underline',
                                    'flex',
                                    'items-center',
                                    'gap-2',
                                    'max-sm:hidden', // hidden-xs replacement
                                    'active:bg-coral-dark',
                                    'valar:active:bg-blue-dark',
                                    'rtl:right-0',
                                    'rtl:left-auto',
                                )}
                                onClick={onFollowLearningBreadcrumbsBack}
                                role="button"
                                tabIndex={0}
                                onKeyDown={e => {
                                    if (e.key === 'Enter' || e.key === ' ') onFollowLearningBreadcrumbsBack();
                                }}
                            >
                                {/* Home back icon - uses inline img instead of background-image */}
                                <img
                                    src={brandStyleClass === 'valar' ? homeBackLargeAlt : homeBackLarge}
                                    alt=""
                                    className={clsx(
                                        // Tailwind (replaces .icon.home)
                                        'w-[29px]',
                                        'h-[29px]',
                                        'rtl:scale-x-[-1]',
                                        brandStyleClass === 'valar' && 'opacity-35',
                                    )}
                                />
                                <span
                                    className={clsx(
                                        // Tailwind (replaces .alternate-home-caption)
                                        'text-primary',
                                        'font-normal',
                                        'text-[25px]',
                                        'leading-none',
                                    )}
                                >
                                    {t('navigation.app_header.back')}
                                </span>
                            </a>
                            <div
                                className={clsx(
                                    // Tailwind (replaces .back, .visible-xs)
                                    'absolute',
                                    'h-10', // $APP_HEADER_HEIGHT_MOBILE
                                    'w-[35px]',
                                    'cursor-pointer',
                                    'sm:hidden', // visible-xs replacement
                                    'active:bg-coral-dark',
                                    'valar:active:bg-blue-dark',
                                    'rtl:-left-2.5',
                                    'rtl:right-auto',
                                )}
                                onClick={onFollowLearningBreadcrumbsBack}
                                role="button"
                                tabIndex={0}
                                onKeyDown={e => {
                                    if (e.key === 'Enter' || e.key === ' ') onFollowLearningBreadcrumbsBack();
                                }}
                            >
                                <img
                                    src={backCaret}
                                    alt="Back"
                                    className={clsx('w-[9px]', 'h-[15px]', 'mt-3', 'ml-2.5', 'rtl:mr-2')}
                                />
                            </div>
                        </>
                    )}

                    {/* Desktop navigation menu - positioned to match Angular app-menu element */}
                    <div
                        className={clsx(
                            // Tailwind positioning - full height for sliding indicator
                            'absolute',
                            'left-[179px]',
                            'min-[830px]:left-[194px]',
                            'md:left-[230px]',
                            'transition-all',
                            'duration-200',
                            'top-0',
                            'bottom-0',
                            'flex',
                            'items-center',
                            'hidden', // Hidden on mobile
                            'sm:flex', // Show on desktop
                            'rtl:right-[179px]',
                            'rtl:min-[830px]:right-[194px]',
                            'rtl:left-auto',
                        )}
                    >
                        {navigationMenu}
                    </div>

                    {/* Mobile settings menu button */}
                    <div
                        className={clsx(
                            'settings-menu', // Keep for test compatibility
                            'absolute',
                            'top-1/2',
                            '-translate-y-1/2',
                            'right-0',
                            'font-semibold',
                            'text-white',
                            'text-2xs',
                            'leading-3',
                            'cursor-pointer',
                            'sm:hidden', // visible-xs replacement
                        )}
                    >
                        <p
                            className={clsx(
                                'button-container', // Keep for test compatibility
                                'h-[25px]',
                                'm-0',
                                'mr-2',
                                'leading-[25px]',
                                'px-2',
                                mobileMenuActive ? 'bg-coral-dark' : 'bg-coral-dark/60',
                                mobileMenuActive ? 'valar:bg-blue-dark' : 'valar:bg-blue-dark/60',
                                'rounded-[3px]',
                                'active:bg-coral-dark',
                                'valar:active:bg-blue-dark',
                                inOfflineMode && 'disabled pointer-events-none cursor-not-allowed opacity-40',
                                // Icon spacing
                                '[&_i]:ms-[5px]',
                            )}
                            onClick={() => !inOfflineMode && onMobileSettingsMenuClick()}
                            role="button"
                            tabIndex={0}
                            onKeyDown={e => {
                                if ((e.key === 'Enter' || e.key === ' ') && !inOfflineMode) {
                                    onMobileSettingsMenuClick();
                                }
                            }}
                        >
                            {t('navigation.app_header.menu_react')} <i className="fa fa-bars" />
                        </p>
                        {/* Triangle pointing down, centered under MENU button */}
                        {mobileMenuActive && (
                            <div
                                className={clsx(
                                    'pointer-events-none',
                                    'absolute',
                                    'bottom-0',
                                    'left-[43%]',
                                    '-translate-x-1/2',
                                    'translate-y-full',
                                    'w-0',
                                    'h-0',
                                    'border-solid',
                                    'border-l-[8px]',
                                    'border-r-[8px]',
                                    'border-b-[8px]',
                                    'border-t-0',
                                    'border-l-transparent',
                                    'border-r-transparent',
                                    'border-t-transparent',
                                    'border-b-slate-grey-900',
                                )}
                            />
                        )}
                    </div>

                    {/* Mobile actions */}
                    <div
                        className={clsx(
                            // Tailwind (replaces .actions)
                            'absolute',
                            'left-10',
                            'lang-ar:left-[25px]',
                            'sm:hidden', // Only show on mobile
                        )}
                    >
                        {/* Share stream */}
                        {isMobileApp && viewedStream && (
                            <img
                                onClick={() => !inOfflineMode && onShareStream()}
                                src={shareIcon}
                                className={clsx(
                                    // Tailwind (replaces .share, .float-end, .visible-xs)
                                    'float-end',
                                    'w-[15px]',
                                    'h-10',
                                    'py-3',
                                    'me-[11px]',
                                    inOfflineMode && 'opacity-30',
                                )}
                                alt="Share"
                                role="button"
                                tabIndex={0}
                                onKeyDown={e => {
                                    if ((e.key === 'Enter' || e.key === ' ') && !inOfflineMode) {
                                        onShareStream();
                                    }
                                }}
                            />
                        )}

                        {/* Bookmark stream */}
                        {viewedStream && viewedStream.favorite && (
                            <img
                                onClick={() => onToggleBookmark(viewedStream)}
                                src={bookmarkChecked}
                                className={clsx(
                                    // Tailwind (replaces .bookmark, .float-end, .visible-xs)
                                    'float-end',
                                    'w-3',
                                    'h-10',
                                    'py-3',
                                    'me-3.5',
                                )}
                                alt="Remove bookmark"
                                role="button"
                                tabIndex={0}
                                onKeyDown={e => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        onToggleBookmark(viewedStream);
                                    }
                                }}
                            />
                        )}
                        {viewedStream && !viewedStream.favorite && (
                            <img
                                onClick={() => onToggleBookmark(viewedStream)}
                                src={bookmarkUnchecked}
                                className={clsx(
                                    // Tailwind (replaces .bookmark, .float-end, .visible-xs)
                                    'float-end',
                                    'w-3',
                                    'h-10',
                                    'py-3',
                                    'me-3.5',
                                )}
                                alt="Add bookmark"
                                role="button"
                                tabIndex={0}
                                onKeyDown={e => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        onToggleBookmark(viewedStream);
                                    }
                                }}
                            />
                        )}
                    </div>

                    {/* messages-button-wrapper class required for Messaging.scss cascade */}
                    {showMessagesButton && (
                        <div
                            className={clsx(
                                'messages-button-wrapper',
                                inOfflineMode && 'pointer-events-none opacity-40',
                            )}
                        >
                            <MessagesButton />
                        </div>
                    )}

                    {/* Desktop user menu - positioned to match Angular app-menu-user element */}
                    <div
                        className={clsx(
                            // Tailwind positioning to match app-menu-user SCSS
                            'absolute',
                            'right-7.5', // 30px
                            'top-1/2',
                            '-translate-y-1/2',
                            'hidden', // Hidden on mobile
                            'sm:block', // Show on desktop (block overrides hidden at sm breakpoint)
                            'rtl:left-7.5',
                            'rtl:right-auto',
                        )}
                    >
                        {userMenu}
                    </div>
                </div>
            </div>
        );
    },
);

// Container component - handles hooks and state, decides layout, delegates rendering
export const AppHeader = memo(() => {
    const {
        // State
        layout,
        hidden,
        title,
        titleOpts,
        showAlternateHomeButton,
        showCaret,
        viewedStream,
        allowTapScroll,
        playerViewModel,
        // Brand config
        brandStyleClass,
        brandNameShort,
        appHeaderLearnerLayoutLogo,
        appHeaderWordMarkSrc,
        appHeaderWordMarkStyles,
        logoAlt,
        // Environment
        inOfflineMode,
        isCordova: isMobileApp,
        // Current user
        currentUser,
        // Navigation
        currentPath,
        // Actions
        goHome,
        followLearningBreadcrumbsBack,
        scrollToTop,
        handleMobileSettingsMenuClick,
        shareStream,
        toggleBookmark,
    } = useAppHeader();

    const ghostMode = useSelector(selectInGhostMode);
    const userEmail = currentUser?.email ?? '';

    const appHeaderClasses = useMemo(
        () =>
            clsx(
                // SCSS classes that must remain:
                // - 'app-header' for .header-invisible .app-header { display: none } rule
                // - brandStyleClass for valar: Tailwind variant to work
                'app-header',
                layout, // Keep for test compatibility (e.g., 'default', 'lesson-player')
                brandStyleClass,
                ghostMode && 'ghost-mode', // Keep for test compatibility
                // Tailwind classes
                'w-full',
                'p-0',
                'm-0',
                // Note: Don't use Tailwind 'block' here - it would override
                // the .header-invisible .app-header { display: none } CSS rule
                'h-10', // 40px mobile
                'sm:h-[75px]', // 75px desktop
            ),
        [layout, brandStyleClass, ghostMode],
    );

    // Hidden state
    if (hidden) return null;

    // Lesson player layout - uses player-header, NOT main-header (Angular template line 165)
    if (layout === 'lesson-player') {
        return (
            <div className={appHeaderClasses}>
                <Suspense fallback={null}>
                    <LessonPlayerHeader ghostMode={ghostMode} userEmail={userEmail} />
                </Suspense>
            </div>
        );
    }

    // Lesson editor layout (hidden in preview mode) - uses lesson-editor-header, NOT main-header (Angular template line 113)
    if (layout === 'lesson-editor') {
        if (playerViewModel?.previewMode) return null;

        return (
            <div className={appHeaderClasses}>
                <Suspense fallback={null}>
                    <LessonEditorHeader ghostMode={ghostMode} userEmail={userEmail} />
                </Suspense>
            </div>
        );
    }

    // Default layout - use presentation component
    return (
        <AppHeaderPresentation
            layout={layout}
            brandStyleClass={brandStyleClass}
            brandNameShort={brandNameShort}
            appHeaderLearnerLayoutLogo={appHeaderLearnerLayoutLogo}
            appHeaderWordMarkSrc={appHeaderWordMarkSrc}
            appHeaderWordMarkStyles={appHeaderWordMarkStyles}
            logoAlt={logoAlt}
            ghostMode={ghostMode}
            userEmail={userEmail}
            title={title ?? ''}
            titleOpts={titleOpts ?? {}}
            showAlternateHomeButton={showAlternateHomeButton}
            showCaret={showCaret}
            viewedStream={viewedStream}
            allowTapScroll={allowTapScroll}
            currentPath={currentPath}
            inOfflineMode={inOfflineMode}
            isMobileApp={isMobileApp}
            onGoHome={goHome}
            onFollowLearningBreadcrumbsBack={followLearningBreadcrumbsBack}
            onScrollToTop={scrollToTop}
            onMobileSettingsMenuClick={handleMobileSettingsMenuClick}
            onShareStream={shareStream}
            onToggleBookmark={toggleBookmark}
            navigationMenu={<AppMenu />}
            userMenu={<AppMenuUser />}
        />
    );
});
